<ng-container *transloco="let t; read: 'grid'">
  <!-- Master + detail sit as SEPARATE rounded cards with a gap between them
       (the detail asides carry the gap as ml-4 so it only exists when a panel
       is present). -->
  <div class="flex h-full min-h-0 w-full">
    <!-- MAIN PANEL — the DS content card: white surface, 16px radius, hairline
         edge. min-h-0 so the table body scrolls internally instead of the
         panel growing and pushing the pagination bar below the fold. In split
         mode it flexes to fill the space left by the fixed-width detail, so
         the extra width on wide screens goes to the master, not the detail. -->
    <!-- @container: the toolbar wraps by the CARD's width, not the viewport's
         — in split mode the master can be narrow on a wide screen. -->
    <div
      class="@container flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-card border border-hairline bg-card"
    >
      <!-- TABS + TOOLBAR -->
      @if (showToolbar()) {
      <!-- Fixed 48px row when the card is wide enough (@3xl = 768px of card);
           narrower it wraps (tabs row, controls row) instead of clipping
           controls off the edge. -->
      <div
        class="flex min-h-12 flex-none flex-wrap items-center justify-between gap-x-4 border-b border-hairline px-4 @max-3xl:py-1.5 @3xl:h-12 @3xl:flex-nowrap @3xl:overflow-hidden @3xl:px-6"
      >
        <div class="flex h-12 min-w-0 flex-shrink items-stretch gap-0.5">
          @if (tabs().length) {
            <!-- DS Tab: Figtree 700 16/20 uppercase; active = ink-1 + 4px
                 action underline; passive = ink-2, hover brand blue. -->
            @for (tab of tabs(); track tab.id) {
              <button
                type="button"
                (click)="onTab(tab.id)"
                class="relative flex h-full items-center gap-2 border-none bg-transparent px-4"
                [class.text-ink-1]="tab.id === activeTabId()"
                [class.text-ink-2]="tab.id !== activeTabId()"
                [class.hover:text-brand]="tab.id !== activeTabId()"
              >
                <span class="whitespace-nowrap font-display text-h3 font-bold uppercase"
                  >{{ tab.labelKey ? t(tab.labelKey) : tab.label }}</span
                >
                @if (tab.dot) {
                  <span
                    class="h-1.5 w-1.5 flex-none rounded-full"
                    style="background: var(--colorscales-red-500)"
                  ></span>
                }
                @if (tab.count !== undefined) {
                  <span
                    class="rounded-pill px-1.5 py-px text-[11px] font-semibold"
                    [class.bg-action]="tab.id === activeTabId()"
                    [class.text-action-text]="tab.id === activeTabId()"
                    [class.bg-sunken]="tab.id !== activeTabId()"
                    [class.text-ink-2]="tab.id !== activeTabId()"
                    >{{ tab.count }}</span
                  >
                }
                @if (tab.id === activeTabId()) {
                  <div class="absolute inset-x-0 -bottom-px h-1 bg-action"></div>
                }
              </button>
            }
          }
        </div>

        <div
          class="flex min-w-0 flex-none flex-wrap items-center gap-2.5 @max-3xl:basis-full @max-3xl:gap-y-1.5 @max-3xl:pb-1.5 @3xl:flex-nowrap"
        >
          <!-- Search — inline with the toolbar controls; shrinks continuously
               on narrow screens instead of holding 240px. -->
          @if (searchable()) {
            <div
              class="flex h-8 min-w-28 max-w-[240px] flex-1 items-center gap-2.5 rounded-control border border-hairline bg-sunken px-3"
            >
              <app-icon name="icon-search" [size]="16" class="text-ink-3" />
              <input
                type="text"
                [value]="searchTerm()"
                (input)="onSearchInput($any($event.target).value)"
                [placeholder]="t('search.placeholder')"
                class="w-full flex-1 border-none bg-transparent text-sm text-ink-1 outline-none"
              />
            </div>
          }

          <!-- Density toggle (DS view-normal / view-condensed glyphs) -->
          <div
            class="flex overflow-hidden rounded-control border border-hairline"
            [title]="t('toolbar.density')"
          >
            <button
              type="button"
              (click)="setDensity('comfortable')"
              [title]="t('toolbar.comfortable')"
              class="flex h-8 w-8 items-center justify-center"
              [class.bg-action]="density() === 'comfortable'"
              [class.text-action-text]="density() === 'comfortable'"
              [class.text-ink-3]="density() !== 'comfortable'"
            >
              <app-icon name="icon-view-normal" [size]="18" />
            </button>
            <button
              type="button"
              (click)="setDensity('compact')"
              [title]="t('toolbar.compact')"
              class="flex h-8 w-8 items-center justify-center"
              [class.bg-action]="density() === 'compact'"
              [class.text-action-text]="density() === 'compact'"
              [class.text-ink-3]="density() !== 'compact'"
            >
              <app-icon name="icon-view-condensed" [size]="18" />
            </button>
          </div>

          <!-- Column visibility -->
          <app-button
            variant="secondary"
            size="sm"
            [label]="t('toolbar.columns')"
            (activated)="toggleColumnMenu($event)"
          />

          @if (exportable()) {
            <app-button
              variant="secondary"
              size="sm"
              iconLeft="icon-export"
              [label]="t('toolbar.exportCsv')"
              (activated)="onExport()"
            />
          }

          @if (primaryAction(); as pa) {
            <app-button
              size="sm"
              [iconLeft]="pa.icon ?? null"
              [label]="pa.labelKey ? t(pa.labelKey) : pa.label"
              (activated)="primaryActionClick.emit()"
            />
          }
        </div>
      </div>
      }

      <!-- Column-visibility menu -->
      @if (columnMenuOpen()) {
        <div class="relative">
          <div
            class="absolute right-6 top-1 z-20 min-w-[200px] rounded-card border border-hairline bg-card p-1.5 shadow-overlay"
          >
            @for (col of toggleableColumns(); track col.key) {
              <button
                type="button"
                (click)="toggleColumn(col.key)"
                [disabled]="col.locked"
                class="flex w-full items-center gap-2.5 rounded-control px-3 py-2 text-left text-[13px] text-ink-1 hover:bg-sunken disabled:opacity-50"
              >
                <span
                  class="flex h-[18px] w-[18px] flex-none items-center justify-center rounded-[5px]"
                  [class.bg-action]="col.visible"
                  [class.border]="!col.visible"
                  [class.border-hairline]="!col.visible"
                >
                  @if (col.visible) {
                    <app-icon name="icon-checkmark" [size]="12" class="text-action-text" />
                  }
                </span>
                {{ col.label }}
              </button>
            }
          </div>
        </div>
      }

      <!-- BULK BAR -->
      @if (selectable() && selectedCount() > 0) {
        <div class="flex flex-none items-center gap-4 bg-bar px-6 py-2.5 text-bar-ink">
          <span class="text-[13px] font-bold">{{
            t('selection.count', { count: selectedCount() })
          }}</span>
          @for (action of bulkActions(); track action.id) {
            <button
              type="button"
              (click)="onBulkAction(action.id)"
              class="flex items-center gap-1.5 rounded-control border px-3 py-1.5 text-[12px] font-semibold hover:bg-bar-ink/10"
              [class.border-hairline]="!action.danger"
              [class.text-bar-ink]="!action.danger"
              [class.border-danger]="action.danger"
              [class.text-danger]="action.danger"
            >
              @if (action.icon) {
                <app-icon [name]="action.icon" [size]="14" />
              }
              {{ action.labelKey ? t(action.labelKey) : action.label }}
            </button>
          }
          <button
            type="button"
            (click)="clearSelection()"
            class="ml-auto border-none bg-transparent px-3 py-1.5 text-[12px] font-semibold text-bar-ink opacity-70 hover:opacity-100"
          >
            {{ t('selection.clear') }}
          </button>
        </div>
      }

      <!-- TABLE (flex-row layout: data columns flex to fill the container and
           reflow when the detail panel opens; thumbnail/select/actions fixed) -->
      <div class="min-h-0 flex-1 overflow-auto" [attr.aria-busy]="loading() || null">
        <div class="px-4" [style.min-width.px]="tableMinWidth()">
          <!-- HEAD — DS HeaderCellFrame: sunken strip, radius 4, caption labels
               bottom-aligned. The sticky wrapper bleeds back over the table
               gutter (-mx-4/px-4) so scrolling rows never peek around the
               strip's rounded corners. -->
          <div class="sticky top-0 z-10 -mx-4 bg-card px-4 py-1.5">
            <div class="flex min-h-8 items-stretch rounded-sm bg-sunken">
              @if (selectable()) {
                <div
                  class="flex flex-none items-center justify-center"
                  [style.width.px]="selectColWidth"
                >
                  <button
                    type="button"
                    (click)="toggleSelectAll()"
                    [attr.aria-label]="t('selection.selectAll')"
                    class="flex h-[18px] w-[18px] items-center justify-center rounded-[5px]"
                    [class.bg-action]="allSelected() || someSelected()"
                    [class.border]="!allSelected() && !someSelected()"
                    [class.border-hairline]="!allSelected() && !someSelected()"
                  >
                    @if (allSelected()) {
                      <app-icon name="icon-checkmark" [size]="12" class="text-action-text" />
                    } @else if (someSelected()) {
                      <span class="h-0.5 w-2.5 rounded bg-action-text"></span>
                    }
                  </button>
                </div>
              }
              @for (col of headerColumns(); track trackByKey($index, col)) {
                <div
                  class="flex min-w-0 items-end gap-1 px-2 py-[11px]"
                  [style.flex]="col.flexCss"
                  [style.minWidth.px]="col.minWidthPx"
                  [class.cursor-pointer]="col.sortable"
                  [class.grid-th-sortable]="col.sortable"
                  [class.justify-end]="col.align === 'right'"
                  [class.justify-center]="col.align === 'center'"
                  (click)="onSort(col.key)"
                >
                  <span class="grid-th-label ds-caption truncate">{{ col.label }}</span>
                  @if (col.sortAsc || col.sortDesc) {
                    <!-- 24px sort glyph clipped to the 16px label line so it
                         doesn't stretch the strip; points down = DESC. -->
                    <span
                      class="grid-th-arrow flex h-4 w-6 flex-none items-center justify-center text-ink-2"
                    >
                      <app-icon
                        name="icon-arrow-dropdown"
                        [size]="24"
                        [class.rotate-180]="col.sortAsc"
                      />
                    </span>
                  }
                </div>
              }
              @if (rowActions().length) {
                <div
                  class="flex flex-none items-end justify-end px-2 py-[11px]"
                  [style.width.px]="actionsColWidth()"
                >
                  <span class="ds-caption">{{ t('actions.header') }}</span>
                </div>
              }
            </div>
          </div>

          <!-- BODY -->
          @for (row of renderRows(); track trackByRow($index, row)) {
            <div
              (click)="onRowClick(row.row)"
              (dblclick)="onRowDblClick(row.row)"
              (contextmenu)="onRowContextMenu(row.id, $event)"
              class="grid-row flex cursor-pointer items-stretch"
              [class.grid-row-selected]="row.id === detailRowId()"
              [class.grid-refetching]="isRefetching()"
              [style.min-height.px]="density() === 'comfortable' ? 64 : 48"
            >
              @if (selectable()) {
                <div
                  class="flex flex-none items-center justify-center"
                  [style.width.px]="selectColWidth"
                >
                  <button
                    type="button"
                    (click)="toggleRow(row.id, $event)"
                    [attr.aria-label]="t('selection.selectAll')"
                    class="flex h-[18px] w-[18px] items-center justify-center rounded-[5px]"
                    [class.bg-action]="row.selected"
                    [class.border]="!row.selected"
                    [class.border-hairline]="!row.selected"
                    [class.bg-card]="!row.selected"
                  >
                    @if (row.selected) {
                      <app-icon name="icon-checkmark" [size]="12" class="text-action-text" />
                    }
                  </button>
                </div>
              }
              @for (cell of row.cells; track trackByKey($index, cell)) {
                <div
                  class="flex min-w-0 items-center overflow-hidden px-2"
                  [style.flex]="cell.flexCss"
                  [style.minWidth.px]="cell.minWidthPx"
                  [class.justify-end]="cell.align === 'right'"
                  [class.justify-center]="cell.align === 'center'"
                >
                  @switch (cell.type) {
                    @case ('template') {
                      @if (cellTemplateMap().get(cell.templateName); as dir) {
                        <ng-container
                          [ngTemplateOutlet]="dir.template"
                          [ngTemplateOutletContext]="cellContext(row.row, cell.value)"
                        />
                      }
                    }
                    @case ('thumbnail') {
                      @if (cell.thumbnailSrc) {
                        <img
                          [src]="cell.thumbnailSrc"
                          alt=""
                          class="flex-none rounded-[5px] object-cover"
                          [style.width.px]="density() === 'comfortable' ? 48 : 32"
                          [style.height.px]="density() === 'comfortable' ? 34 : 22"
                        />
                      } @else {
                        <div
                          class="flex-none rounded-[5px] bg-sunken"
                          [style.width.px]="density() === 'comfortable' ? 48 : 32"
                          [style.height.px]="density() === 'comfortable' ? 34 : 22"
                        ></div>
                      }
                    }
                    @case ('status') {
                      @if (cell.status; as st) {
                        <span class="inline-flex min-w-0 items-center gap-2.5 text-base text-ink-1">
                          <span
                            class="h-2.5 w-2.5 flex-none rounded-full"
                            [class.border]="st.hollow"
                            [class.border-strong]="st.hollow"
                            [class.bg-success]="!st.hollow && st.color === 'green'"
                            [class.bg-warning]="!st.hollow && st.color === 'orange'"
                            [class.bg-danger]="!st.hollow && st.color === 'red'"
                            [class.bg-caution]="!st.hollow && st.color === 'yellow'"
                            [class.bg-brand]="!st.hollow && st.color === 'blue'"
                            [class.bg-ink-3]="!st.hollow && st.color === 'neutral'"
                          ></span>
                          <span class="truncate">{{ st.label }}</span>
                        </span>
                      }
                    }
                    @case ('date') {
                      <!-- Null dates render a dash — "no data", never t('empty')'s
                           "No results" (that's the empty-table state). -->
                      <span class="truncate text-base font-semibold text-ink-2">{{
                        cell.value ? ($any(cell.value) | date: 'medium') : t('emptyValue')
                      }}</span>
                    }
                    @case ('boolean') {
                      <span class="truncate text-base font-semibold text-ink-2">{{
                        cell.value === undefined || cell.value === null
                          ? t('empty')
                          : cell.value
                            ? t('boolean.true')
                            : t('boolean.false')
                      }}</span>
                    }
                    @case ('json') {
                      <span class="truncate text-base font-semibold text-ink-2">{{
                        cell.value ? (cell.value | json) : t('empty')
                      }}</span>
                    }
                    @default {
                      <!-- DS CellFrame text roles: primary = OS 400 16/24
                           ink-1, secondary = OS 600 16/24 ink-2. -->
                      <span
                        class="truncate text-base"
                        [class.text-ink-1]="cell.textVariant === 'strong'"
                        [class.font-semibold]="cell.textVariant === 'normal'"
                        [class.text-ink-2]="cell.textVariant === 'normal'"
                        [class.text-ink-3]="cell.textVariant === 'muted'"
                        >{{ cell.value ?? t('empty') }}</span
                      >
                    }
                  }
                </div>
              }
              @if (rowActions().length) {
                <!-- DS ControlsCellFrame: right-aligned 24px icon buttons in
                     the action ink. -->
                <div
                  class="relative flex flex-none items-center justify-end gap-2 px-2"
                  [style.width.px]="actionsColWidth()"
                >
                  @for (action of inlineActions(); track action.id) {
                    <button
                      type="button"
                      (click)="onRowAction(action.id, row.row, $event)"
                      [disabled]="action.disabled ? action.disabled(row.row) : false"
                      [title]="action.labelKey ? t(action.labelKey) : action.label"
                      class="flex h-6 w-6 flex-none items-center justify-center disabled:opacity-40"
                      [class.text-danger]="action.danger"
                      [class.text-action]="!action.danger"
                      [class.hover:text-brand]="!action.danger"
                    >
                      <app-icon [name]="action.icon ?? 'icon-placeholder'" [size]="24" />
                    </button>
                  }
                  @if (menuActions().length) {
                    <app-action-menu
                      [items]="menuItemsFor(row.row)"
                      [triggerTitle]="t('actions.more')"
                      [anchor]="menuAnchor()"
                      [open]="openMenuRowId() === row.id"
                      (openChange)="setRowMenuOpen(row.id, $event)"
                      (itemSelect)="onRowAction($event, row.row)"
                    />
                  }
                </div>
              }
            </div>
          } @empty {
            @if (showSkeleton()) {
              <!-- Cold load: bone rows on the real column geometry, so nothing
                   shifts when data lands. Decorative — hidden from AT (the
                   scroll container carries aria-busy). -->
              <div class="grid-skeleton" aria-hidden="true">
                @for (bones of skeletonRows(); track $index) {
                  <div
                    class="flex items-stretch border-b border-hairline"
                    [style.min-height.px]="density() === 'comfortable' ? 64 : 48"
                  >
                    @if (selectable()) {
                      <div class="flex-none" [style.width.px]="selectColWidth"></div>
                    }
                    @for (cell of bones; track cell.key) {
                      <div
                        class="flex min-w-0 items-center gap-2.5 overflow-hidden px-2"
                        [style.flex]="cell.flexCss"
                        [style.minWidth.px]="cell.minWidthPx"
                        [class.justify-end]="cell.align === 'right'"
                        [class.justify-center]="cell.align === 'center'"
                      >
                        @switch (cell.type) {
                          @case ('thumbnail') {
                            <div
                              class="flex-none animate-pulse rounded-[5px] bg-surface-2"
                              [style.width.px]="density() === 'comfortable' ? 48 : 32"
                              [style.height.px]="density() === 'comfortable' ? 34 : 22"
                            ></div>
                          }
                          @case ('status') {
                            <span
                              class="h-2.5 w-2.5 flex-none animate-pulse rounded-full bg-surface-2"
                            ></span>
                            <span class="h-4 w-14 animate-pulse rounded bg-surface-2"></span>
                          }
                          @case ('date') {
                            <span class="h-4 w-3/5 animate-pulse rounded bg-surface-2"></span>
                          }
                          @default {
                            <span
                              class="h-4 animate-pulse rounded bg-surface-2"
                              [style.width.%]="cell.boneWidthPct"
                            ></span>
                          }
                        }
                      </div>
                    }
                    @if (rowActions().length) {
                      <div class="flex-none" [style.width.px]="actionsColWidth()"></div>
                    }
                  </div>
                }
              </div>
            } @else {
              <div class="flex h-60 items-center justify-center text-sm text-ink-3">
                {{ t('empty') }}
              </div>
            }
          }
        </div>
      </div>

      <!-- PAGINATION — DS Pagination: prev / "1-20 / 100" / next cluster left,
           small text-dropdown per-page selector right. -->
      <div
        class="flex h-[54px] flex-none items-center justify-between border-t border-hairline px-4 sm:px-6"
      >
        <div class="flex items-center gap-2.5">
          <button
            type="button"
            (click)="prevPage()"
            [disabled]="!canPrev()"
            [title]="t('pagination.previous')"
            class="flex h-6 w-6 items-center justify-center rounded-control text-ink-1 enabled:hover:text-brand disabled:text-ink-3"
          >
            <app-icon name="icon-arrow-left" [size]="24" />
          </button>
          <span class="whitespace-nowrap text-base"
            ><span class="text-ink-2">{{
              t('pagination.rangeCurrent', { from: rangeFrom(), to: rangeTo() })
            }}</span
            ><span class="text-ink-1">{{
              t('pagination.rangeTotal', { total: totalElements() })
            }}</span></span
          >
          <button
            type="button"
            (click)="nextPage()"
            [disabled]="!canNext()"
            [title]="t('pagination.next')"
            class="flex h-6 w-6 items-center justify-center rounded-control text-ink-1 enabled:hover:text-brand disabled:text-ink-3"
          >
            <app-icon name="icon-arrow-right" [size]="24" />
          </button>
        </div>
        <div class="flex items-center gap-2.5">
          <select
            (change)="onPageSizeChange($any($event.target).value)"
            class="h-8 rounded-control border-none bg-transparent pr-1 text-base text-ink-1 hover:text-brand"
          >
            @for (option of pageSizeOptions(); track option) {
              <option [value]="option" [selected]="option === pageSize()">
                {{ t('pagination.perPage', { count: option }) }}
              </option>
            }
          </select>
        </div>
      </div>
    </div>

    <!-- DETAIL PANEL — overlay: slides in once a row is selected and slides
         back out on dismiss (animate.leave defers DOM removal to animationend).
         The animation drives the aside's width so the table columns reflow in
         sync with the slide; the fixed-width inner wrapper keeps the content
         laid out while the clipping shell is mid-animation. -->
    @if (showOverlayDetail()) {
      <aside
        animate.enter="grid-panel-in"
        animate.leave="grid-panel-out"
        class="ml-4 flex-none overflow-hidden rounded-card border border-hairline bg-card"
        [style.width]="detailWidthCss()"
        [style.--grid-detail-width]="detailWidthCss()"
      >
        <!-- w-full, not detailWidthCss: a percentage here would resolve
             against the aside itself. Content reflows during the 260ms slide,
             which is fine. -->
        <div class="flex h-full w-full flex-col">
          <div
            class="flex h-[58px] flex-none items-center justify-between border-b border-hairline px-5"
          >
            <span class="text-[15px] font-bold text-ink-1">{{ t('detail.title') }}</span>
            <button
              type="button"
              (click)="closeDetail()"
              [title]="t('detail.close')"
              class="flex h-[30px] w-[30px] items-center justify-center rounded-control border-none bg-transparent text-ink-3 hover:bg-sunken"
            >
              <app-icon name="icon-x" [size]="17" />
            </button>
          </div>
          <div class="min-h-0 flex-1 overflow-auto p-5">
            @if (detailTemplate(); as dir) {
              <ng-container
                [ngTemplateOutlet]="dir.template"
                [ngTemplateOutletContext]="{ $implicit: detailRow(), row: detailRow() }"
              />
            }
          </div>
        </div>
      </aside>
    }

    <!-- DETAIL PANEL — split: persistent fixed-width pane; the master flexes to
         take any extra width on wide screens. -->
    @if (showSplitDetail()) {
      <aside
        class="ml-4 flex flex-none flex-col overflow-hidden rounded-card border border-hairline bg-card"
        [style.width]="detailWidthCss()"
      >
        @if (detailRow() !== null) {
          <div class="min-h-0 flex-1 overflow-auto">
            @if (detailTemplate(); as dir) {
              <ng-container
                [ngTemplateOutlet]="dir.template"
                [ngTemplateOutletContext]="{ $implicit: detailRow(), row: detailRow() }"
              />
            }
          </div>
        } @else {
          <div class="flex min-h-0 flex-1 items-center justify-center p-8 text-center text-sm text-ink-3">
            {{ t('detail.selectPrompt') }}
          </div>
        }
      </aside>
    }

    <!-- DETAIL PANEL — sheet: below lg every detail mode renders as a
         full-width bottom sheet over the page (a fixed-width side panel would
         starve the table). Sibling @ifs so scrim and sheet each root their own
         view for animate.leave. -->
    @if (showSheetDetail()) {
      <div
        class="fixed inset-0 z-40 bg-scrim-control"
        animate.enter="grid-scrim-in"
        animate.leave="grid-scrim-out"
        (click)="closeDetail()"
        aria-hidden="true"
      ></div>
    }
    @if (showSheetDetail()) {
      <section
        role="dialog"
        aria-modal="true"
        animate.enter="grid-sheet-in"
        animate.leave="grid-sheet-out"
        class="fixed inset-x-0 bottom-0 top-14 z-40 flex flex-col overflow-hidden rounded-t-card border border-hairline bg-card shadow-modal"
      >
        <div
          class="flex h-[58px] flex-none items-center justify-between border-b border-hairline px-5"
        >
          <span class="text-[15px] font-bold text-ink-1">{{ t('detail.title') }}</span>
          <button
            type="button"
            (click)="closeDetail()"
            [title]="t('detail.close')"
            class="flex h-[30px] w-[30px] items-center justify-center rounded-control border-none bg-transparent text-ink-3 hover:bg-sunken"
          >
            <app-icon name="icon-x" [size]="17" />
          </button>
        </div>
        <div class="min-h-0 flex-1 overflow-auto p-5">
          @if (detailTemplate(); as dir) {
            <ng-container
              [ngTemplateOutlet]="dir.template"
              [ngTemplateOutletContext]="{ $implicit: detailRow(), row: detailRow() }"
            />
          }
        </div>
      </section>
    }
  </div>
</ng-container>
